import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Button, Modal, Form, Input, InputNumber,
  Select, Space, Tag, Typography, message, Popconfirm, Switch,
  Tabs, Tooltip, Empty, Spin, Badge, Statistic, Pagination, Upload,
} from 'antd';
import {
  SafetyCertificateOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  BookOutlined, FileTextOutlined, QuestionCircleOutlined, SearchOutlined,
  BulbOutlined, CalculatorOutlined, ReadOutlined, ApiOutlined,
  ExperimentOutlined, CodeOutlined, TrophyOutlined, ClockCircleOutlined,
  StarOutlined, CheckCircleOutlined, LockOutlined, TagsOutlined,
  PictureOutlined, CloseCircleFilled,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';
const BASE = `${API_BASE_URL}/govt`;

async function apiFetch(path: string, token: string, opts?: RequestInit) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-App-ID': 'meritxgov',
      ...(opts?.headers ?? {}),
    },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

const DIFFICULTY_META: Record<string, { color: string; bg: string; label: string }> = {
  easy:   { color: '#059669', bg: '#ECFDF5', label: 'Easy' },
  medium: { color: '#D97706', bg: '#FFFBEB', label: 'Medium' },
  hard:   { color: '#DC2626', bg: '#FEF2F2', label: 'Hard' },
};

const SUBJECT_ICONS: Record<string, React.ReactNode> = {
  GK:  <BulbOutlined />,
  KHC: <BookOutlined />,
  IPC: <SafetyCertificateOutlined />,
  ENG: <ReadOutlined />,
  MAT: <CalculatorOutlined />,
  RSN: <ApiOutlined />,
  SCI: <ExperimentOutlined />,
  CMP: <CodeOutlined />,
};

const SUBJECT_COLORS = [
  { accent: '#1D4ED8', bg: '#EFF6FF' },
  { accent: '#059669', bg: '#ECFDF5' },
  { accent: '#7C3AED', bg: '#F5F3FF' },
  { accent: '#D97706', bg: '#FFFBEB' },
  { accent: '#DC2626', bg: '#FEF2F2' },
  { accent: '#0891B2', bg: '#ECFEFF' },
  { accent: '#BE185D', bg: '#FDF2F8' },
  { accent: '#065F46', bg: '#D1FAE5' },
];

const GovtExamsPage: React.FC = () => {
  const { user } = useAuth();
  const [token, setToken]           = useState('');
  const [exams, setExams]           = useState<any[]>([]);
  const [subjects, setSubjects]     = useState<any[]>([]);
  const [questions, setQuestions]   = useState<any[]>([]);
  const [qTotal, setQTotal]         = useState(0);
  const [qPage, setQPage]           = useState(1);
  const [tests, setTests]           = useState<any[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [loading, setLoading]       = useState(false);
  const [qSearch, setQSearch]       = useState('');
  const [activeTab, setActiveTab]   = useState('subjects');

  // Modals
  const [examModal, setExamModal]       = useState(false);
  const [subjectModal, setSubjectModal] = useState(false);
  const [questionModal, setQuestionModal] = useState(false);
  const [testModal, setTestModal]       = useState(false);
  const [editingExam, setEditingExam]     = useState<any>(null);
  const [editingSubject, setEditingSubject] = useState<any>(null);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const [editingTest, setEditingTest]     = useState<any>(null);

  const [examForm]     = Form.useForm();
  const [subjectForm]  = Form.useForm();
  const [questionForm] = Form.useForm();
  const [testForm]     = Form.useForm();

  // Topics
  const [topicModal, setTopicModal]           = useState(false);
  const [topicModalSubject, setTopicModalSubject] = useState<any>(null);
  const [subjectTopics, setSubjectTopics]     = useState<any[]>([]);
  const [topicsLoading, setTopicsLoading]     = useState(false);
  const [newTopicName, setNewTopicName]       = useState('');
  const [questionTopics, setQuestionTopics]   = useState<any[]>([]);
  const questionSubjectId = Form.useWatch('subject_id', questionForm);

  // Question images (question_image, option_a_image..option_d_image)
  const [imagePreviews, setImagePreviews]   = useState<Record<string, string>>({});
  const [imageUploading, setImageUploading] = useState<Record<string, boolean>>({});

  const uploadQuestionImage = async (field: string, file: File) => {
    setImageUploading(prev => ({ ...prev, [field]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/admin/upload/image?folder=questions', formData, {
        headers: { 'Content-Type': undefined },
      });
      const { key, url } = r.data.data;
      questionForm.setFieldValue(field, key);
      setImagePreviews(prev => ({ ...prev, [field]: url }));
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Image upload failed');
    } finally {
      setImageUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const removeQuestionImage = (field: string) => {
    questionForm.setFieldValue(field, undefined);
    setImagePreviews(prev => { const next = { ...prev }; delete next[field]; return next; });
  };

  // When opening an existing question, resolve any stored image keys into
  // previewable URLs (they're private S3 keys, not directly browsable).
  const resolveQuestionImagePreviews = async (q: any) => {
    const fields = ['question_image', 'option_a_image', 'option_b_image', 'option_c_image', 'option_d_image'];
    const entries = await Promise.all(fields.map(async (f) => {
      if (!q[f]) return null;
      try {
        const r = await api.get('/admin/upload/resolve', { params: { key: q[f] } });
        return [f, r.data.data.url] as const;
      } catch { return null; }
    }));
    setImagePreviews(Object.fromEntries(entries.filter(Boolean) as [string, string][]));
  };

  useEffect(() => { user?.getIdToken().then(setToken); }, [user]);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [ex, st] = await Promise.all([
        apiFetch('/admin/exams', token),
        apiFetch('/admin/stats', token),
      ]);
      const examList = Array.isArray(ex) ? ex : [];
      setExams(examList);
      setStats(st);
      if (examList.length && !selectedExamId) setSelectedExamId(examList[0].id);
    } catch { message.error('Failed to load exams'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const loadExamData = async (examId: string) => {
    if (!token || !examId) return;
    try {
      const [subs, ts] = await Promise.all([
        apiFetch(`/admin/subjects?examId=${examId}`, token),
        apiFetch(`/admin/tests?examId=${examId}`, token),
      ]);
      setSubjects(Array.isArray(subs) ? subs : []);
      setTests(Array.isArray(ts) ? ts : []);
    } catch { message.error('Failed to load exam data'); }
    loadQuestions(1, examId);
  };

  useEffect(() => { if (selectedExamId) loadExamData(selectedExamId); }, [token, selectedExamId]); // eslint-disable-line

  const loadQuestions = async (page = 1, examId = selectedExamId) => {
    if (!token || !examId) return;
    try {
      const r = await apiFetch(
        `/admin/questions?examId=${examId}&page=${page}&limit=12${qSearch ? `&search=${encodeURIComponent(qSearch)}` : ''}`,
        token
      );
      const rows = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : []);
      setQuestions(rows);
      setQTotal(r?.total ?? rows.length);
      setQPage(page);
    } catch {}
  };

  // ── Exam CRUD ────────────────────────────────────────────────────
  const saveExam = async () => {
    const v = await examForm.validateFields();
    try {
      if (editingExam) {
        await apiFetch(`/admin/exams/${editingExam.id}`, token, { method: 'PUT', body: JSON.stringify(v) });
        message.success('Exam updated');
      } else {
        await apiFetch('/admin/exams', token, { method: 'POST', body: JSON.stringify(v) });
        message.success('Exam created');
      }
      setExamModal(false);
      load();
    } catch (e: any) { message.error(e.message); }
  };

  // ── Subject CRUD ─────────────────────────────────────────────────
  const saveSubject = async () => {
    const v = await subjectForm.validateFields();
    try {
      if (editingSubject) {
        await apiFetch(`/admin/subjects/${editingSubject.id}`, token, { method: 'PUT', body: JSON.stringify(v) });
        message.success('Subject updated');
      } else {
        await apiFetch('/admin/subjects', token, {
          method: 'POST', body: JSON.stringify({ ...v, exam_id: selectedExamId }),
        });
        message.success('Subject created');
      }
      setSubjectModal(false);
      const subs = await apiFetch(`/admin/subjects?examId=${selectedExamId}`, token);
      setSubjects(Array.isArray(subs) ? subs : []);
    } catch (e: any) { message.error(e.message); }
  };

  const deleteSubject = async (id: string) => {
    try {
      await apiFetch(`/admin/subjects/${id}`, token, { method: 'DELETE' });
      message.success('Subject deleted');
      const subs = await apiFetch(`/admin/subjects?examId=${selectedExamId}`, token);
      setSubjects(Array.isArray(subs) ? subs : []);
    } catch { message.error('Delete failed'); }
  };

  // ── Question CRUD ────────────────────────────────────────────────
  const saveQuestion = async () => {
    const v = await questionForm.validateFields();
    try {
      if (editingQuestion) {
        await apiFetch(`/admin/questions/${editingQuestion.id}`, token, { method: 'PUT', body: JSON.stringify(v) });
        message.success('Question updated');
      } else {
        await apiFetch('/admin/questions', token, {
          method: 'POST', body: JSON.stringify({ ...v, exam_id: selectedExamId }),
        });
        message.success('Question created');
      }
      setQuestionModal(false);
      loadQuestions(qPage);
    } catch (e: any) { message.error(e.message); }
  };

  const deleteQuestion = async (id: string) => {
    try {
      await apiFetch(`/admin/questions/${id}`, token, { method: 'DELETE' });
      message.success('Deleted');
      loadQuestions(qPage);
    } catch { message.error('Delete failed'); }
  };

  // ── Test CRUD ────────────────────────────────────────────────────
  const saveTest = async () => {
    const v = await testForm.validateFields();
    try {
      if (editingTest) {
        await apiFetch(`/admin/tests/${editingTest.id}`, token, { method: 'PUT', body: JSON.stringify(v) });
        message.success('Test updated');
      } else {
        await apiFetch('/admin/tests', token, {
          method: 'POST', body: JSON.stringify({ ...v, exam_id: selectedExamId }),
        });
        message.success('Test created');
      }
      setTestModal(false);
      const ts = await apiFetch(`/admin/tests?examId=${selectedExamId}`, token);
      setTests(Array.isArray(ts) ? ts : []);
    } catch (e: any) { message.error(e.message); }
  };

  const deleteTest = async (id: string) => {
    try {
      await apiFetch(`/admin/tests/${id}`, token, { method: 'DELETE' });
      message.success('Deleted');
      const ts = await apiFetch(`/admin/tests?examId=${selectedExamId}`, token);
      setTests(Array.isArray(ts) ? ts : []);
    } catch { message.error('Delete failed'); }
  };

  // ── Topics ───────────────────────────────────────────────────────
  const openTopicsModal = async (subject: any) => {
    setTopicModalSubject(subject);
    setNewTopicName('');
    setTopicModal(true);
    setTopicsLoading(true);
    try {
      const t = await apiFetch(`/admin/topics?subjectId=${subject.id}`, token);
      setSubjectTopics(Array.isArray(t) ? t : []);
    } catch { message.error('Failed to load topics'); }
    finally { setTopicsLoading(false); }
  };

  const addTopic = async () => {
    if (!newTopicName.trim() || !topicModalSubject) return;
    try {
      await apiFetch('/admin/topics', token, {
        method: 'POST',
        body: JSON.stringify({ subjectId: topicModalSubject.id, name: newTopicName.trim() }),
      });
      setNewTopicName('');
      const t = await apiFetch(`/admin/topics?subjectId=${topicModalSubject.id}`, token);
      setSubjectTopics(Array.isArray(t) ? t : []);
      if (topicModalSubject.id === questionSubjectId) {
        setQuestionTopics(Array.isArray(t) ? t : []);
      }
    } catch { message.error('Failed to add topic'); }
  };

  const deleteTopicItem = async (id: string) => {
    try {
      await apiFetch(`/admin/topics/${id}`, token, { method: 'DELETE' });
      const t = await apiFetch(`/admin/topics?subjectId=${topicModalSubject.id}`, token);
      setSubjectTopics(Array.isArray(t) ? t : []);
      if (topicModalSubject.id === questionSubjectId) {
        setQuestionTopics(Array.isArray(t) ? t : []);
      }
    } catch { message.error('Failed to delete topic'); }
  };

  useEffect(() => {
    if (!token || !questionSubjectId) { setQuestionTopics([]); return; }
    apiFetch(`/admin/topics?subjectId=${questionSubjectId}`, token)
      .then(t => setQuestionTopics(Array.isArray(t) ? t : []))
      .catch(() => setQuestionTopics([]));
  }, [token, questionSubjectId]);

  const selectedExam = exams.find(e => e.id === selectedExamId);

  // ── Render helpers ───────────────────────────────────────────────
  const ImagePicker: React.FC<{ field: string; label: string }> = ({ field, label }) => {
    const value = Form.useWatch(field, questionForm);
    const preview = imagePreviews[field];
    const busy = !!imageUploading[field];
    return (
      <div style={{ marginBottom: 12 }}>
        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>{label}</Text>
        {value ? (
          <div style={{ position: 'relative', width: 88, height: 88 }}>
            {preview ? (
              <img src={preview} alt={label} style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
            ) : (
              <div style={{ width: 88, height: 88, borderRadius: 8, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
                <Spin size="small" />
              </div>
            )}
            <CloseCircleFilled
              onClick={() => removeQuestionImage(field)}
              style={{ position: 'absolute', top: -6, right: -6, color: '#DC2626', background: '#fff', borderRadius: '50%', fontSize: 16, cursor: 'pointer' }}
            />
          </div>
        ) : (
          <Upload
            showUploadList={false}
            accept="image/jpeg,image/png,image/webp,image/gif"
            customRequest={({ file }) => uploadQuestionImage(field, file as File)}
            disabled={busy}>
            <div style={{
              width: 88, height: 88, borderRadius: 8, border: '1px dashed #D1D5DB',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              color: '#9CA3AF', cursor: busy ? 'default' : 'pointer', gap: 4,
            }}>
              {busy ? <Spin size="small" /> : <><PictureOutlined style={{ fontSize: 18 }} /><span style={{ fontSize: 11 }}>Add image</span></>}
            </div>
          </Upload>
        )}
      </div>
    );
  };

  const SubjectsGrid = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Subjects</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({subjects.length} configured)</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setEditingSubject(null); subjectForm.resetFields(); setSubjectModal(true); }}
          style={{ borderRadius: 8 }}>
          Add Subject
        </Button>
      </div>
      {subjects.length === 0 ? (
        <Empty
          image={<BookOutlined style={{ fontSize: 48, color: '#D1D5DB' }} />}
          description={<Text type="secondary">No subjects yet — add one to get started</Text>}
          style={{ padding: '48px 0' }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {subjects.map((s, i) => {
            const palette = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
            const icon = SUBJECT_ICONS[s.code] ?? <BookOutlined />;
            return (
              <Col xs={24} sm={12} lg={8} key={s.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: 16,
                    border: 'none',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'all 0.2s',
                  }}
                  bodyStyle={{ padding: '20px 24px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                        background: palette.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, color: palette.accent,
                      }}>
                        {icon}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 15, display: 'block', lineHeight: '20px', marginBottom: 4 }}>
                          {s.name}
                        </Text>
                        <Tag style={{
                          borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 11,
                          background: palette.bg, color: palette.accent,
                        }}>
                          {s.code}
                        </Tag>
                      </div>
                    </div>
                    <Space size={4} style={{ flexShrink: 0, marginLeft: 8 }}>
                      <Tooltip title="Manage Topics">
                        <Button
                          type="text" size="small" icon={<TagsOutlined />}
                          style={{ color: '#6B7280' }}
                          onClick={() => openTopicsModal(s)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          type="text" size="small" icon={<EditOutlined />}
                          style={{ color: '#6B7280' }}
                          onClick={() => { setEditingSubject(s); subjectForm.setFieldsValue(s); setSubjectModal(true); }}
                        />
                      </Tooltip>
                      <Popconfirm title="Delete this subject?" onConfirm={() => deleteSubject(s.id)} okType="danger">
                        <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    </Space>
                  </div>
                  <div style={{
                    marginTop: 16, paddingTop: 14,
                    borderTop: `1px solid ${palette.bg}`,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: palette.accent }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Sort order: {s.sort_order ?? i}
                    </Text>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );

  const QuestionsPanel = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Questions Bank</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({qTotal} total)</Text>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />}
          onClick={() => {
            setEditingQuestion(null);
            questionForm.resetFields();
            questionForm.setFieldsValue({ exam_id: selectedExamId, marks: 1, negative_marks: 0, difficulty: 'medium' });
            setImagePreviews({});
            setQuestionModal(true);
          }}
          style={{ borderRadius: 8 }}>
          Add Question
        </Button>
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <Input
          placeholder="Search questions…"
          prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
          value={qSearch}
          onChange={e => setQSearch(e.target.value)}
          onPressEnter={() => loadQuestions(1)}
          style={{ borderRadius: 10, flex: 1 }}
          allowClear
          onClear={() => { setQSearch(''); loadQuestions(1); }}
        />
        <Button onClick={() => loadQuestions(1)} style={{ borderRadius: 10 }} icon={<SearchOutlined />}>
          Search
        </Button>
      </div>

      {questions.length === 0 ? (
        <Empty
          image={<QuestionCircleOutlined style={{ fontSize: 48, color: '#D1D5DB' }} />}
          description={<Text type="secondary">No questions yet — add the first question</Text>}
          style={{ padding: '48px 0' }}
        />
      ) : (
        <Row gutter={[12, 12]}>
          {questions.map((q, i) => {
            const diff = DIFFICULTY_META[q.difficulty] ?? DIFFICULTY_META.medium;
            const subj = subjects.find(s => s.id === q.subject_id);
            const palette = SUBJECT_COLORS[subjects.findIndex(s => s.id === q.subject_id) % SUBJECT_COLORS.length] ?? SUBJECT_COLORS[0];
            return (
              <Col xs={24} key={q.id}>
                <Card
                  style={{
                    borderRadius: 12,
                    border: '1px solid #F3F4F6',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                  bodyStyle={{ padding: '16px 20px' }}
                  hoverable
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: '#F9FAFB', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: '#9CA3AF', fontWeight: 700, fontSize: 13,
                    }}>
                      {(qPage - 1) * 12 + i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ display: 'block', fontSize: 14, lineHeight: '20px', marginBottom: 10 }}>
                        {q.question}
                      </Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <Tag style={{
                          borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                          background: diff.bg, color: diff.color,
                        }}>
                          {diff.label}
                        </Tag>
                        {subj && (
                          <Tag style={{
                            borderRadius: 6, border: 'none', fontSize: 11,
                            background: palette.bg, color: palette.accent,
                          }}>
                            {subj.name}
                          </Tag>
                        )}
                        <Tag style={{ borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 11 }}>
                          {q.marks ?? 1} mark{q.marks !== 1 ? 's' : ''}
                        </Tag>
                      </div>
                    </div>
                    <Space size={4} style={{ flexShrink: 0 }}>
                      <Button
                        type="text" size="small" icon={<EditOutlined />}
                        style={{ color: '#6B7280' }}
                        onClick={() => {
                          setEditingQuestion(q);
                          questionForm.setFieldsValue(q);
                          setImagePreviews({});
                          resolveQuestionImagePreviews(q);
                          setQuestionModal(true);
                        }}
                      />
                      <Popconfirm title="Delete this question?" onConfirm={() => deleteQuestion(q.id)} okType="danger">
                        <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    </Space>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {qTotal > 12 && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Pagination
            current={qPage} total={qTotal} pageSize={12}
            onChange={p => loadQuestions(p)}
            showTotal={(t, r) => `${r[0]}–${r[1]} of ${t}`}
          />
        </div>
      )}
    </div>
  );

  const TestsGrid = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Mock Tests</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({tests.length} total)</Text>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />}
          onClick={() => {
            setEditingTest(null);
            testForm.resetFields();
            testForm.setFieldsValue({ total_questions: 100, duration_minutes: 90, total_marks: 100, negative_marks: 0, is_free: true, is_active: true });
            setTestModal(true);
          }}
          style={{ borderRadius: 8 }}>
          Create Test
        </Button>
      </div>

      {tests.length === 0 ? (
        <Empty
          image={<FileTextOutlined style={{ fontSize: 48, color: '#D1D5DB' }} />}
          description={<Text type="secondary">No mock tests yet — create the first one</Text>}
          style={{ padding: '48px 0' }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {tests.map(t => (
            <Col xs={24} sm={12} lg={8} key={t.id}>
              <Card
                hoverable
                style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}
                bodyStyle={{ padding: 0 }}
              >
                {/* Card header */}
                <div style={{
                  background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
                  borderRadius: '16px 16px 0 0', padding: '20px 20px 16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'rgba(255,255,255,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, color: '#fff',
                    }}>
                      <FileTextOutlined />
                    </div>
                    <Space size={6}>
                      <Tag style={{
                        borderRadius: 20, border: 'none', fontWeight: 700, fontSize: 10,
                        background: t.is_free ? 'rgba(16,185,129,0.2)' : 'rgba(124,58,237,0.3)',
                        color: t.is_free ? '#6EE7B7' : '#C4B5FD',
                        padding: '2px 10px',
                      }}>
                        {t.is_free ? 'FREE' : 'PREMIUM'}
                      </Tag>
                      <Tag style={{
                        borderRadius: 20, border: 'none', fontSize: 10,
                        background: t.is_active ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)',
                        color: t.is_active ? '#6EE7B7' : 'rgba(255,255,255,0.5)',
                        padding: '2px 10px',
                      }}>
                        {t.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </Tag>
                    </Space>
                  </div>
                  <Text strong style={{ color: '#fff', fontSize: 15, display: 'block', marginTop: 14, lineHeight: '20px' }}>
                    {t.title}
                  </Text>
                </div>

                {/* Card body */}
                <div style={{ padding: '16px 20px' }}>
                  <Row gutter={0}>
                    {[
                      { icon: <QuestionCircleOutlined />, label: 'Questions', value: t.total_questions ?? t.question_count ?? '—' },
                      { icon: <ClockCircleOutlined />, label: 'Duration', value: `${t.duration_minutes ?? '—'} min` },
                      { icon: <TrophyOutlined />, label: 'Total Marks', value: t.total_marks ?? '—' },
                      { icon: <StarOutlined />, label: 'Attempts', value: t.attempt_count ?? 0 },
                    ].map(stat => (
                      <Col span={12} key={stat.label} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#9CA3AF', fontSize: 13 }}>{stat.icon}</span>
                          <div>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block', lineHeight: '14px' }}>{stat.label}</Text>
                            <Text strong style={{ fontSize: 13 }}>{stat.value}</Text>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Button
                      size="small" icon={<EditOutlined />}
                      style={{ flex: 1, borderRadius: 8, height: 32 }}
                      onClick={() => { setEditingTest(t); testForm.setFieldsValue(t); setTestModal(true); }}>
                      Edit
                    </Button>
                    <Popconfirm title="Delete this test?" onConfirm={() => deleteTest(t.id)} okType="danger">
                      <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 8, height: 32, width: 32 }} />
                    </Popconfirm>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );

  if (loading && exams.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" tip="Loading govt exam data…" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#0F172A' }}>
              <SafetyCertificateOutlined style={{ color: '#1D4ED8', marginRight: 10 }} />
              Govt Exam Management
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Manage exams, subjects, question banks and mock tests
            </Text>
          </div>
          <Button
            type="primary" icon={<PlusOutlined />} size="large"
            style={{ borderRadius: 10, height: 44, paddingInline: 20 }}
            onClick={() => { setEditingExam(null); examForm.resetFields(); setExamModal(true); }}>
            New Exam
          </Button>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        {[
          { label: 'Govt Exams',      value: exams.length,                color: '#1D4ED8', bg: '#EFF6FF', icon: <SafetyCertificateOutlined /> },
          { label: 'Subjects',        value: subjects.length,             color: '#059669', bg: '#ECFDF5', icon: <BookOutlined /> },
          { label: 'Total Questions', value: stats?.totalQuestions ?? 0,  color: '#D97706', bg: '#FFFBEB', icon: <QuestionCircleOutlined /> },
          { label: 'Mock Tests',      value: stats?.totalTests ?? 0,      color: '#7C3AED', bg: '#F5F3FF', icon: <FileTextOutlined /> },
        ].map(s => (
          <Col xs={12} lg={6} key={s.label}>
            <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
              bodyStyle={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: s.bg, color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                }}>
                  {s.icon}
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{s.label}</Text>
                  <Text strong style={{ fontSize: 24, color: s.color, lineHeight: '30px' }}>{s.value}</Text>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Exam selector chips ── */}
      <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: 24 }}
        bodyStyle={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text strong style={{ color: '#6B7280', fontSize: 13, whiteSpace: 'nowrap' }}>Select Exam:</Text>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
            {exams.map(e => {
              const active = e.id === selectedExamId;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelectedExamId(e.id)}
                  style={{
                    padding: '8px 18px', borderRadius: 24, cursor: 'pointer',
                    border: active ? 'none' : '1.5px solid #E5E7EB',
                    background: active ? 'linear-gradient(135deg, #1D4ED8, #2563EB)' : '#fff',
                    color: active ? '#fff' : '#374151',
                    fontWeight: active ? 700 : 500, fontSize: 13,
                    boxShadow: active ? '0 4px 12px rgba(29,78,216,0.3)' : 'none',
                    transition: 'all 0.2s',
                  }}>
                  {e.name}
                  {e.code && <span style={{ marginLeft: 6, opacity: 0.7, fontWeight: 400 }}>({e.code})</span>}
                </button>
              );
            })}
          </div>
          {selectedExamId && (
            <Button
              size="small" icon={<EditOutlined />}
              style={{ borderRadius: 8, flexShrink: 0 }}
              onClick={() => {
                const e = exams.find(x => x.id === selectedExamId);
                setEditingExam(e);
                examForm.setFieldsValue(e);
                setExamModal(true);
              }}>
              Edit Exam
            </Button>
          )}
        </div>

        {selectedExam && (
          <div style={{
            marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6',
            display: 'flex', gap: 24, flexWrap: 'wrap',
          }}>
            {[
              { label: 'Questions', value: selectedExam.total_questions ?? '—' },
              { label: 'Duration', value: selectedExam.duration_minutes ? `${selectedExam.duration_minutes} min` : '—' },
              { label: 'Status', value: selectedExam.is_active !== false ? 'Active' : 'Inactive' },
            ].map(m => (
              <div key={m.label}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{m.label}</Text>
                <Text strong style={{ fontSize: 13 }}>{m.value}</Text>
              </div>
            ))}
            {selectedExam.description && (
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Description</Text>
                <Text style={{ fontSize: 13 }}>{selectedExam.description}</Text>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Content tabs ── */}
      <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
        bodyStyle={{ padding: '8px 24px 24px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          items={[
            {
              key: 'subjects',
              label: (
                <span>
                  <BookOutlined />
                  Subjects
                  <Badge count={subjects.length} style={{ marginLeft: 8, backgroundColor: '#1D4ED8' }} overflowCount={99} />
                </span>
              ),
              children: <SubjectsGrid />,
            },
            {
              key: 'questions',
              label: (
                <span>
                  <QuestionCircleOutlined />
                  Questions
                  <Badge count={qTotal} style={{ marginLeft: 8, backgroundColor: '#D97706' }} overflowCount={9999} />
                </span>
              ),
              children: <QuestionsPanel />,
            },
            {
              key: 'tests',
              label: (
                <span>
                  <FileTextOutlined />
                  Mock Tests
                  <Badge count={tests.length} style={{ marginLeft: 8, backgroundColor: '#7C3AED' }} overflowCount={99} />
                </span>
              ),
              children: <TestsGrid />,
            },
          ]}
        />
      </Card>

      {/* ── Exam Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>{editingExam ? 'Edit Exam' : 'Create New Exam'}</span>}
        open={examModal} onOk={saveExam} onCancel={() => setExamModal(false)}
        okText={editingExam ? 'Update' : 'Create'} width={520}
        styles={{ body: { paddingTop: 16 } }}>
        <Form form={examForm} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Exam Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input placeholder="e.g. KSP Police Constable" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="code" label="Short Code" rules={[{ required: true }]}>
                <Input placeholder="KSP_PC" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="total_questions" label="Total Questions">
                <InputNumber style={{ width: '100%', borderRadius: 8 }} min={1} placeholder="100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="duration_minutes" label="Duration (min)">
                <InputNumber style={{ width: '100%', borderRadius: 8 }} min={1} placeholder="90" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Subject Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>{editingSubject ? 'Edit Subject' : 'Add Subject'}</span>}
        open={subjectModal} onOk={saveSubject} onCancel={() => setSubjectModal(false)}
        okText={editingSubject ? 'Update' : 'Add'} width={460}
        styles={{ body: { paddingTop: 16 } }}>
        <Form form={subjectForm} layout="vertical">
          <Form.Item name="name" label="Subject Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Karnataka History & Culture" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="code" label="Subject Code">
                <Input placeholder="KHC" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order">
                <InputNumber style={{ width: '100%', borderRadius: 8 }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Question Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>{editingQuestion ? 'Edit Question' : 'Add Question'}</span>}
        open={questionModal} onOk={saveQuestion} onCancel={() => setQuestionModal(false)}
        okText={editingQuestion ? 'Update' : 'Add'} width={700}
        styles={{ body: { paddingTop: 16 } }}>
        <Form form={questionForm} layout="vertical">
          <Form.Item name="question" label="Question" rules={[{ required: true }]}>
            <TextArea rows={3} placeholder="Enter the question text" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="question_image" noStyle><Input type="hidden" /></Form.Item>
          <ImagePicker field="question_image" label="Question image (optional)" />

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="option_a" label="Option A" rules={[{ required: true }]}><Input style={{ borderRadius: 8 }} /></Form.Item>
              <Form.Item name="option_a_image" noStyle><Input type="hidden" /></Form.Item>
              <ImagePicker field="option_a_image" label="Image (optional)" />
            </Col>
            <Col span={12}>
              <Form.Item name="option_b" label="Option B" rules={[{ required: true }]}><Input style={{ borderRadius: 8 }} /></Form.Item>
              <Form.Item name="option_b_image" noStyle><Input type="hidden" /></Form.Item>
              <ImagePicker field="option_b_image" label="Image (optional)" />
            </Col>
            <Col span={12}>
              <Form.Item name="option_c" label="Option C" rules={[{ required: true }]}><Input style={{ borderRadius: 8 }} /></Form.Item>
              <Form.Item name="option_c_image" noStyle><Input type="hidden" /></Form.Item>
              <ImagePicker field="option_c_image" label="Image (optional)" />
            </Col>
            <Col span={12}>
              <Form.Item name="option_d" label="Option D" rules={[{ required: true }]}><Input style={{ borderRadius: 8 }} /></Form.Item>
              <Form.Item name="option_d_image" noStyle><Input type="hidden" /></Form.Item>
              <ImagePicker field="option_d_image" label="Image (optional)" />
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="answer" label="Correct Answer" rules={[{ required: true }]}>
                <Select options={['A','B','C','D'].map(v => ({ value: v, label: `Option ${v}` }))} style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="difficulty" label="Difficulty">
                <Select options={['easy','medium','hard'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }))} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="marks" label="Marks"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="negative_marks" label="Negative Marks"><InputNumber style={{ width: '100%' }} step={0.25} min={0} /></Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="subject_id" label="Subject">
                <Select
                  options={subjects.map(s => ({ value: s.id, label: s.name }))}
                  allowClear placeholder="Select a subject"
                  onChange={() => questionForm.setFieldValue('topic_id', undefined)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="topic_id" label="Topic">
                <Select
                  options={questionTopics.map(t => ({ value: t.id, label: t.name }))}
                  allowClear
                  placeholder={questionSubjectId ? 'Select a topic' : 'Select a subject first'}
                  disabled={!questionSubjectId}
                  notFoundContent={questionSubjectId ? 'No topics yet — add one from Subjects' : null}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="explanation" label="Explanation (optional)">
            <TextArea rows={2} style={{ borderRadius: 8 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Topics Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>Topics — {topicModalSubject?.name}</span>}
        open={topicModal} onCancel={() => setTopicModal(false)}
        footer={<Button onClick={() => setTopicModal(false)}>Close</Button>}
        width={440}>
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="New topic name"
            value={newTopicName}
            onChange={e => setNewTopicName(e.target.value)}
            onPressEnter={addTopic}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={addTopic}>Add</Button>
        </Space.Compact>
        {topicsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}><Spin /></div>
        ) : subjectTopics.length === 0 ? (
          <Text type="secondary">No topics yet for this subject.</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {subjectTopics.map(t => (
              <div key={t.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', borderRadius: 8, background: '#F9FAFB',
              }}>
                <Text>{t.name}</Text>
                <Popconfirm title="Delete this topic?" onConfirm={() => deleteTopicItem(t.id)} okType="danger">
                  <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                </Popconfirm>
              </div>
            ))}
          </Space>
        )}
      </Modal>

      {/* ── Test Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>{editingTest ? 'Edit Mock Test' : 'Create Mock Test'}</span>}
        open={testModal} onOk={saveTest} onCancel={() => setTestModal(false)}
        okText={editingTest ? 'Update' : 'Create'} width={520}
        styles={{ body: { paddingTop: 16 } }}>
        <Form form={testForm} layout="vertical">
          <Form.Item name="title" label="Test Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. KSP PC Full Mock Test #1" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="total_questions" label="Questions"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="duration_minutes" label="Duration (min)"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="total_marks" label="Total Marks"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
          </Row>
          <Form.Item name="negative_marks" label="Negative marks per wrong answer">
            <InputNumber style={{ width: 160 }} step={0.25} min={0} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_free" label="Free Access" valuePropName="checked">
                <Switch checkedChildren={<CheckCircleOutlined />} unCheckedChildren={<LockOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default GovtExamsPage;
